/* =====================================================================
   AstaLab 2.0 — externalRecordsEngine.js (round alpha.2, obiettivo 8)

   Porting VERBATIM del reconciler centrale beta.6
   (reconcileAllExternalRecords/mergeExternalRecordGroup/
   resolveExternalRecordPlayer/detectDuplicateExternalIdAssociations,
   astalab.html righe 3254-3626) — nessuna riga di logica decisionale
   cambiata: stessi cinque esiti di resolveExternalRecordPlayer()
   (matched/both, matched/fantacalcioId, matched/externalId con
   canonicalizzazione, identity-conflict, ambiguous), stessa regola D
   (selezione esplicita quando più fonti sono attive), stesso
   auto-recupero quando un record diventa l'unica fonte disponibile per
   la sua combinazione.

   Unico adattamento (mai di comportamento, solo di forma): `candidate`
   della 1.3 diventa qui `catalogState` — la stessa identica forma
   {players, externalStatistics, unassociatedExternalStatistics} — e non
   esiste più un fallback impostazione `state||STATE` su un global: ogni
   funzione richiede `state` esplicito, come nel resto del sottosistema
   catalogo (vedi statisticsEngine.js). commitBusinessMutation()/
   setActiveExternalSource() (wiring alla persistenza) restano di
   competenza di CatalogRepository — questo modulo resta puro.
   ===================================================================== */
import { toStr, nowISO } from "./normalizeUtils.js";
import { parseSeasonKey } from "./statisticsEngine.js";

export const EXTERNAL_SOURCE_WHITELIST = ["transfermarkt-manual","csv-manual","provider-authorized"];

function toIntegerOrNullLocal(v, min, max){
  if(v===null || v===undefined || v==="") return null;
  const n = typeof v==="number" ? v : parseFloat(String(v).replace(",", "."));
  if(!isFinite(n)) return null;
  return Math.max(min, Math.min(max, Math.round(n)));
}

// Revisione r3 (blocker 8, terzo audit): range NUMERICI condivisi con private validazione server-side
// di import_external_statics() in 0008_catalog_rpc_import.sql — PRIMA di questa revisione i due
// backend usavano massimi diversi per lo stesso campo (es. appearances: qui 200, lato SQL 80), quindi
// lo STESSO CSV poteva essere accettato dall'Artifact demo e rifiutato dal deploy reale (o viceversa).
// Calibrati sullo stesso ordine di grandezza dei range delle statistiche UFFICIALI (pv 0..60, gf/assist/
// ammonizioni 0..200, espulsioni/rigori 0..50/100 — vedi import_statistics_archive), con un margine
// per un provider esterno che possa aggregare più competizioni nella stessa stagione. Import ONE
// unico ESPLICITO qui e nella RPC — mai un numero magico duplicato scollegato — chi tocca un valore
// deve aggiornarlo in ENTRAMBI i posti (nessun modulo condiviso possibile fra JS runtime e PL/pgSQL).
export const EXTERNAL_STATISTICS_RANGES = {
  appearances: [0, 100], starts: [0, 100], minutes: [0, 12000],
  goals: [0, 200], assists: [0, 200], yellowCards: [0, 200], redCards: [0, 50],
  penaltiesScored: [0, 100], penaltiesMissed: [0, 100], cleanSheets: [0, 100], goalsConceded: [0, 300]
};

export function normalizeExternalStatisticsRecord(raw){
  const src = raw && typeof raw==="object" ? raw : {};
  const R = EXTERNAL_STATISTICS_RANGES;
  return {
    id: toStr(src.id, "ext_"+Math.random().toString(36).slice(2,10)),
    fantacalcioId: toIntegerOrNullLocal(src.fantacalcioId, 0, 100000000),
    // Revisione r4 (audit, blocker 1): parte del contratto canonico condiviso con
    // SupabaseCatalogRepository (external_statistics.player_id) — un record appena importato non
    // ha ancora un giocatore risolto, popolato SOLO da reconcileAllExternalRecords() quando la
    // risoluzione trova un match reale (mai qui, che non ha visibilità sul catalogo).
    playerId: src.playerId!=null ? toStr(src.playerId, null) : null,
    nome: toStr(src.nome, ""),
    season: toStr(src.season, ""),
    source: toStr(src.source, ""),
    // Revisione r3 (blocker 9, terzo audit): canonicalizzato QUI, non solo nel confronto locale di
    // resolveExternalRecordPlayer() più sotto — prima di questa revisione il record PERSISTITO
    // poteva restare non-trimmato anche se la risoluzione usava una copia trimmata solo per il
    // confronto, quindi "ext-123" e " ext-123 " potevano finire in due gruppi/chiavi DISTINTI
    // (externalRecordKey usa direttamente rec.sourcePlayerId) invece di essere la stessa identità.
    sourcePlayerId: toStr(src.sourcePlayerId, "").trim(),
    sourceUrl: toStr(src.sourceUrl, ""),
    team: toStr(src.team, ""),
    competition: toStr(src.competition, ""),
    appearances: toIntegerOrNullLocal(src.appearances, R.appearances[0], R.appearances[1]),
    starts: toIntegerOrNullLocal(src.starts, R.starts[0], R.starts[1]),
    minutes: toIntegerOrNullLocal(src.minutes, R.minutes[0], R.minutes[1]),
    goals: toIntegerOrNullLocal(src.goals, R.goals[0], R.goals[1]),
    assists: toIntegerOrNullLocal(src.assists, R.assists[0], R.assists[1]),
    yellowCards: toIntegerOrNullLocal(src.yellowCards, R.yellowCards[0], R.yellowCards[1]),
    redCards: toIntegerOrNullLocal(src.redCards, R.redCards[0], R.redCards[1]),
    penaltiesScored: toIntegerOrNullLocal(src.penaltiesScored, R.penaltiesScored[0], R.penaltiesScored[1]),
    penaltiesMissed: toIntegerOrNullLocal(src.penaltiesMissed, R.penaltiesMissed[0], R.penaltiesMissed[1]),
    cleanSheets: toIntegerOrNullLocal(src.cleanSheets, R.cleanSheets[0], R.cleanSheets[1]),
    goalsConceded: toIntegerOrNullLocal(src.goalsConceded, R.goalsConceded[0], R.goalsConceded[1]),
    notes: toStr(src.notes, ""),
    active: src.active===undefined ? true : !!src.active,
    // Revisione r3 (audit): timestamp di arrivo, usato SOLO per ordinare cronologicamente un merge
    // di record duplicati (mergeExternalRecordGroup) — mai per decidere l'identità del giocatore.
    // Preservato se già presente (un record che rientra nel pool ad un reconcile successivo non
    // deve mai vedersi assegnare un nuovo istante, altrimenti perderebbe il proprio ordine reale).
    importedAt: toStr(src.importedAt, nowISO()),
    // Revisione alpha.3-r1 (fondamenta enrichment provider-neutral): identità/versione OPZIONALE
    // lato provider — assente per ogni riga csv-manual/transfermarkt-manual (mai un valore
    // fabbricato qui). Preservata SE già presente (stesso principio di importedAt sopra: un record
    // che rientra nel pool a un reconcile successivo non deve mai perdere la propria identità
    // provider). Vedi mergeExternalRecordGroup() per come questi campi vengono usati.
    // Revisione alpha.3-r3 (audit blocker C su r2): providerKey CANONICALIZZATO (trim) qui — l'unico
    // punto in cui un record esterno viene normalizzato/persistito. Prima di questa revisione questo
    // campo non veniva mai trimmato qui (solo buildVendorScopedSourcePlayerId() in
    // providerEnrichmentEngine.js lo faceva), quindi " vendor-a " e "vendor-a" producevano lo stesso
    // sourcePlayerId (namespacing già trimmato) ma un providerKey/group_key DIVERSO nel record
    // persistito stesso — esattamente la divergenza trovata dall'audit. SQL era già corretto
    // (btrim() in import_external_statistics) — qui la stessa regola si applica anche lato JS.
    providerKey: src.providerKey!=null ? (toStr(src.providerKey, "").trim() || null) : null,
    providerObservationId: src.providerObservationId!=null ? toStr(src.providerObservationId, null).trim() || null : null,
    providerObservedAt: src.providerObservedAt!=null ? toStr(src.providerObservedAt, null) : null,
    providerPayloadChecksum: src.providerPayloadChecksum!=null ? toStr(src.providerPayloadChecksum, null) : null,
    // Revisione alpha.3-r3 (audit blocker A su r2): ordine di RICEZIONE reale, monotono, MAI un id
    // locale/UUID casuale — assegnato da assignReceiptSequences() (mai qui: questa funzione non ha
    // visibilità su un contatore persistito) e SEMPRE preservato se già presente, esattamente come
    // importedAt sopra — un record che rientra nel pool a un reconcile successivo non deve mai
    // perdere il proprio ordine di ricezione reale. `null` finché non assegnato (mai un default
    // silenzioso qui: vedi receiptSeqOf() per il fallback usato SOLO in lettura).
    receiptSeq: src.receiptSeq!=null ? src.receiptSeq : null
  };
}

// Revisione alpha.3-r3 (audit blocker A su r2): assegna un ordine di RICEZIONE reale, monotono e mai
// casuale a ogni record che non ne abbia già uno (preservando quelli già presenti — stesso principio
// di importedAt/providerObservedAt), a partire da `startSeq` e in base all'ordine con cui i record
// compaiono nell'array `records` (l'ordine reale con cui questo sistema li ha ricevuti in QUESTO
// batch — un CSV o un batch di osservazioni provider). Il chiamante (CatalogRepository) possiede lo
// stato persistito (`catalogState.nextReceiptSeq`) e deve salvare `nextSeq` per il prossimo batch —
// questo modulo resta puro, nessun contatore globale nascosto.
export function assignReceiptSequences(records, startSeq){
  let seq = typeof startSeq==="number" && isFinite(startSeq) ? startSeq : 1;
  const out = (records||[]).map(rec=>{
    if(rec.receiptSeq!=null) return rec;
    const withSeq = Object.assign({}, rec, { receiptSeq: seq });
    seq += 1;
    return withSeq;
  });
  return { records: out, nextSeq: seq };
}
function receiptSeqOf(record){
  return record && record.receiptSeq!=null ? record.receiptSeq : 0;
}

// ---------------------------------------------------------------------
// Revisione r4 (audit, blocker 2): PRIMA di questa revisione l'unico "validatore" dell'import CSV
// esterno era normalizeExternalStatisticsRecord() sopra, che CLAMPA (Math.max/Math.min) invece di
// rifiutare — un CSV con appearances=101 diventava silenziosamente appearances=100, MAI un errore,
// nonostante il rapporto r3 dichiarasse il contrario. normalizeExternalStatisticsRecord() resta
// disponibile per la canonicalizzazione/manutenzione di record GIÀ esistenti (dove un clamp
// difensivo su dati che hanno già passato l'import è accettabile), ma NON deve più essere il
// cancello che decide se un CSV in importazione è valido. EXTERNAL_STATISTICS_CSV_COLUMNS e
// parseAndValidateExternalStatisticsCSV() sono la pipeline STRICT condivisa, usata da ENTRAMBI i
// repository (demo e Supabase) — un valore mancante è NULL (consentito), un valore presente ma
// non numerico/frazionario-dove-serve-intero/fuori range fa fallire l'INTERO file: nessun record
// viene aggiunto, nessun checksum segnato come importato, nessun reconcile, nessuna modifica.
// ---------------------------------------------------------------------
export const EXTERNAL_STATISTICS_CSV_COLUMNS = ["fantacalcioId","nome","season","source","sourcePlayerId","sourceUrl","team","competition",
  "appearances","starts","minutes","goals","assists","yellowCards","redCards","penaltiesScored",
  "penaltiesMissed","cleanSheets","goalsConceded","notes"];

const EXTERNAL_STATISTICS_INTEGER_FIELDS = ["appearances","starts","minutes","goals","assists",
  "yellowCards","redCards","penaltiesScored","penaltiesMissed","cleanSheets","goalsConceded"];

function isBlankCsvValue(v){ return v==null || String(v).trim()===""; }

function parseStrictNumber(v){
  const s = String(v).trim().replace(",", ".");
  if(s==="") return { blank:true };
  const n = Number(s);
  if(!isFinite(n)) return { error:true };
  return { value:n };
}

/** Valida UNA riga già estratta dal CSV (chiavi = EXTERNAL_STATISTICS_CSV_COLUMNS). Restituisce un
 *  messaggio d'errore descrittivo (mai un booleano opaco) o null se la riga è valida. Mai un clamp:
 *  o il valore rientra esattamente nel range/formato atteso, o l'intero file viene rifiutato. */
export function validateExternalStatisticsCsvRow(raw, rowNumber){
  const label = "Riga " + rowNumber;
  const fidBlank = isBlankCsvValue(raw.fantacalcioId);
  const extIdBlank = isBlankCsvValue(raw.sourcePlayerId);
  if(fidBlank && extIdBlank){
    return label + ": né fantacalcioId né sourcePlayerId sono presenti (riga non identificabile).";
  }
  if(!fidBlank){
    const parsed = parseStrictNumber(raw.fantacalcioId);
    if(parsed.error) return label + ": fantacalcioId non numerico (\"" + raw.fantacalcioId + "\").";
    if(parsed.value !== Math.floor(parsed.value) || parsed.value <= 0){
      return label + ": fantacalcioId deve essere un intero positivo (\"" + raw.fantacalcioId + "\").";
    }
  }
  if(isBlankCsvValue(raw.season) || !parseSeasonKey(String(raw.season).trim())){
    return label + ": season non valida o mancante, formato atteso AAAA_AA (\"" + raw.season + "\").";
  }
  if(isBlankCsvValue(raw.source) || !EXTERNAL_SOURCE_WHITELIST.includes(String(raw.source).trim())){
    return label + ": source non consentita (\"" + raw.source + "\").";
  }
  for(const field of EXTERNAL_STATISTICS_INTEGER_FIELDS){
    if(isBlankCsvValue(raw[field])) continue;
    const parsed = parseStrictNumber(raw[field]);
    if(parsed.error) return label + ": " + field + " non numerico (\"" + raw[field] + "\").";
    if(parsed.value !== Math.floor(parsed.value)){
      return label + ": " + field + " deve essere un intero, non frazionario (\"" + raw[field] + "\").";
    }
    const range = EXTERNAL_STATISTICS_RANGES[field];
    if(parsed.value < range[0] || parsed.value > range[1]){
      return label + ": " + field + " fuori range (" + range[0] + ".." + range[1] + "): " + parsed.value + ".";
    }
  }
  return null;
}

/** Pipeline di parsing+validazione STRICT per un CSV di record esterni, condivisa da
 *  ArtifactDemoCatalogRepository e SupabaseCatalogRepository (obiettivo 11: stesso contratto, mai
 *  una regola duplicata/divergente fra i due backend). `parseCSVFn`/`detectDelimiterFn` sono
 *  iniettate (da statisticsEngine.js) invece di importate qui per evitare un ciclo di import fra i
 *  moduli catalogo. Ritorna SEMPRE {ok, error, records}: se una sola riga è invalida, `ok` è false,
 *  `error` descrive ESATTAMENTE quale riga e quale campo, e `records` è vuoto — mai un risultato
 *  parziale. `records` contiene oggetti già canonicalizzati via normalizeExternalStatisticsRecord()
 *  (a questo punto un puro passo di shaping: i valori sono già stati validati, il clamp non scatta
 *  mai su un input che ha superato questa pipeline). */
export function parseAndValidateExternalStatisticsCSV(text, parseCSVFn, detectDelimiterFn){
  const delim = detectDelimiterFn(text) === ";" ? ";" : ",";
  const rows = parseCSVFn(text, delim);
  if(!rows.length) return { ok:false, error:"Il file non contiene righe valide.", records:[] };
  const headers = rows[0].map(h => String(h||"").trim());
  const idxByField = {};
  EXTERNAL_STATISTICS_CSV_COLUMNS.forEach(f=>{
    const idx = headers.findIndex(h => h.toLowerCase()===f.toLowerCase());
    if(idx>=0) idxByField[f] = idx;
  });
  if(idxByField.fantacalcioId==null || idxByField.season==null || idxByField.source==null){
    return { ok:false, error:"Intestazioni non riconosciute: sono richieste almeno fantacalcioId, season, source.", records:[] };
  }

  const records = [];
  let rowNumber = 1;
  for(const row of rows.slice(1)){
    rowNumber++;
    if(!row.some(c => c!=="")) continue;
    const raw = {};
    EXTERNAL_STATISTICS_CSV_COLUMNS.forEach(f=>{ if(idxByField[f]!=null) raw[f] = row[idxByField[f]]; });
    const rowError = validateExternalStatisticsCsvRow(raw, rowNumber);
    if(rowError) return { ok:false, error:rowError, records:[] };
    records.push(normalizeExternalStatisticsRecord(raw));
  }
  if(!records.length) return { ok:false, error:"Nessun record valido nel file.", records:[] };
  return { ok:true, error:null, records };
}

// Revisione alpha.3-r3 (audit blocker B su r2): "provider-authorized" è un livello di fiducia
// condiviso da QUALUNQUE vendor autorizzato — un giocatore deve poter avere un'associazione manuale
// PER VENDOR contemporaneamente (player+vendor-A e player+vendor-B, mai una sostituisce l'altra).
// `player.externalIds` resta uno scalare {source: valore} per csv-manual/transfermarkt-manual
// (INVARIATO, un solo valore per fonte, mai toccato da questa revisione).
//
// Revisione alpha.3-r4 (terzo audit, blocker 3): per "provider-authorized" la r3 usava un oggetto
// {providerKey: valore} — ma un providerKey NULL (associazione legacy, vendor sconosciuto) non può
// mai essere rappresentato come chiave di un oggetto JS: `{[null]: valore}` coercizza SEMPRE la
// chiave alla STRINGA letterale "null" (`Object.keys` la restituirebbe come "null", indistinguibile
// da un vendor reale chiamato "null"). Corretto sostituendo l'oggetto con un ARRAY di entry
// `{providerKey, value}` — `providerKey` resta un valore di CAMPO, mai una chiave, quindi `null`
// resta realmente `null` (mai una stringa). externalIdEntries() appiattisce OGNI forma (array
// multi-vendor, scalare legacy pre-vendor-scoping) in un'unica lista uniforme {source, providerKey,
// value} — usata da ogni punto che deve leggere/confrontare un'associazione, mai una seconda
// implementazione divergente. Una stringa scalare pre-esistente sotto "provider-authorized" (scritta
// da fixture/test/stato prima della vendor-scoping) resta tollerata in LETTURA come una singola
// associazione con providerKey=null — nessuna migrazione distruttiva richiesta, nessun test/
// regressione precedente rotto.
export function isVendorScopedExternalSource(source){ return source==="provider-authorized"; }

// Confronto NULL-safe fra due "slot" di providerKey (mai una stringa "null" — sempre un vero null o
// una stringa non vuota, per costruzione di normalizeExternalStatisticsRecord()/externalIdEntries()
// sotto). Usata ovunque due identità vendor-scoped devono essere confrontate: legacy (null) contro
// legacy (null) -> stessa identità (comportamento invariato); un vendor reale contro un vendor
// DIVERSO (o legacy) -> mai la stessa identità.
export function sameProviderKey(a, b){
  return (a==null ? null : a) === (b==null ? null : b);
}

export function externalIdEntries(player){
  const map = (player && player.externalIds) || {};
  const entries = [];
  Object.keys(map).forEach(source=>{
    const v = map[source];
    if(v==null) return;
    if(isVendorScopedExternalSource(source)){
      if(Array.isArray(v)){
        v.forEach(entry=>{
          if(entry && entry.value) entries.push({source, providerKey: entry.providerKey!=null ? entry.providerKey : null, value: entry.value});
        });
      }else if(typeof v==="string" && v){
        // Forma scalare legacy (pre-vendor-scoping, r1/r2-era): un'unica associazione, vendor
        // sconosciuto -> providerKey REALMENTE null, mai la stringa "null".
        entries.push({source, providerKey:null, value: v});
      }
    }else if(typeof v==="string" && v){
      entries.push({source, providerKey:null, value: v});
    }
  });
  return entries;
}

function externalIdListOf(player, source){
  const existing = player.externalIds && player.externalIds[source];
  if(Array.isArray(existing)) return existing.slice();
  if(typeof existing==="string" && existing) return [{providerKey:null, value:existing}];
  return [];
}

/** Scrive UNA associazione (aggiungendo/sostituendo, mai cancellando le altre) — per una fonte
 *  vendor-scoped, `providerKey` (già canonico: trim) seleziona quale entry dell'array viene scritta,
 *  per VALORE (mai per chiave d'oggetto — vedi commento sopra, blocker 3); una stringa scalare
 *  pre-esistente (forma legacy) viene convertita in array SOLO al primo write, preservando la sua
 *  entry originale (providerKey=null) se il nuovo `providerKey` è diverso. Per una fonte non
 *  vendor-scoped resta lo scalare invariato di sempre. */
export function setExternalIdEntry(player, source, providerKey, value){
  const slot = providerKey==null ? null : providerKey;
  if(isVendorScopedExternalSource(source)){
    const list = externalIdListOf(player, source);
    const idx = list.findIndex(e=>sameProviderKey(e.providerKey, slot));
    const nextEntry = {providerKey: slot, value};
    if(idx>=0) list[idx] = nextEntry; else list.push(nextEntry);
    player.externalIds = Object.assign({}, player.externalIds, { [source]: list });
  }else{
    player.externalIds = Object.assign({}, player.externalIds, { [source]: value });
  }
}

/** Rimuove UNA associazione. Per una fonte vendor-scoped rimuove SOLO l'entry il cui `providerKey`
 *  corrisponde (le altre associazioni vendor sullo stesso player restano intatte) — una forma
 *  scalare legacy pre-esistente viene rimossa solo quando il chiamante non specifica un providerKey
 *  (mirror del comportamento "rimuovi per source" di sempre). Ritorna `true` se qualcosa è stato
 *  realmente rimosso (mai un `false` silenzioso travestito da successo). */
export function removeExternalIdEntry(player, source, providerKey){
  const slot = providerKey==null ? null : providerKey;
  if(isVendorScopedExternalSource(source)){
    const list = externalIdListOf(player, source);
    const idx = list.findIndex(e=>sameProviderKey(e.providerKey, slot));
    if(idx<0) return false;
    const updatedList = list.slice(0, idx).concat(list.slice(idx+1));
    const updated = Object.assign({}, player.externalIds);
    if(updatedList.length===0) delete updated[source]; else updated[source] = updatedList;
    player.externalIds = updated;
    return true;
  }
  if(!player.externalIds || !player.externalIds[source]) return false;
  const updated = Object.assign({}, player.externalIds);
  delete updated[source];
  player.externalIds = updated;
  return true;
}

export function resolveExternalRecordPlayer(catalogState, rec){
  const players = catalogState.players||[];
  const fantacalcioIdPlayer = rec.fantacalcioId!=null ? (players.find(p=>p.fantacalcioId===rec.fantacalcioId) || null) : null;
  const sourcePlayerId = toStr(rec.sourcePlayerId,"").trim();
  // Revisione alpha.3-r4 (terzo audit, obiettivo architetturale + blocker 4): sourcePlayerId è ora
  // SEMPRE l'id grezzo del provider (mai più namespacizzato "providerKey:idGrezzo", vedi
  // providerEnrichmentEngine.js) — due vendor diversi possono quindi legittimamente condividere lo
  // STESSO valore grezzo per due giocatori diversi. Il match richiede ora ESPLICITAMENTE anche
  // sameProviderKey(e.providerKey, rec.providerKey) insieme all'uguaglianza di `value` — mai una
  // corrispondenza per il solo valore. Per csv-manual/transfermarkt-manual (rec.providerKey sempre
  // null, e.providerKey sempre null per costruzione) questo equivale esattamente al confronto di
  // prima (comportamento legacy invariato).
  const byExternalId = sourcePlayerId ? players.filter(p=> externalIdEntries(p).some(e=>e.source===rec.source && e.value===sourcePlayerId && sameProviderKey(e.providerKey, rec.providerKey))) : [];

  if(byExternalId.length>1){
    return {status:"ambiguous", player:null, matchedBy:null,
      fantacalcioIdPlayer, externalIdPlayer:null,
      reason:"L'identificativo \""+sourcePlayerId+"\" su "+rec.source+" è associato a più di un giocatore corrente ("+byExternalId.map(p=>p.nome).join(", ")+")."};
  }
  const externalIdPlayer = byExternalId[0] || null;

  if(fantacalcioIdPlayer && externalIdPlayer){
    if(fantacalcioIdPlayer.id===externalIdPlayer.id){
      return {status:"matched", player:fantacalcioIdPlayer, matchedBy:"both", fantacalcioIdPlayer, externalIdPlayer, reason:null};
    }
    return {status:"identity-conflict", player:null, matchedBy:null, fantacalcioIdPlayer, externalIdPlayer,
      reason:"fantacalcioId "+rec.fantacalcioId+" identifica "+fantacalcioIdPlayer.nome+", mentre "+rec.source+"/"+sourcePlayerId+" è associato a "+externalIdPlayer.nome+"."};
  }
  if(fantacalcioIdPlayer && !externalIdPlayer){
    return {status:"matched", player:fantacalcioIdPlayer, matchedBy:"fantacalcioId", fantacalcioIdPlayer, externalIdPlayer:null, reason:null};
  }
  if(!fantacalcioIdPlayer && externalIdPlayer){
    return {status:"matched", player:externalIdPlayer, matchedBy:"externalId", fantacalcioIdPlayer:null, externalIdPlayer, reason:null};
  }
  return {status:"unmatched", player:null, matchedBy:null, fantacalcioIdPlayer:null, externalIdPlayer:null,
    reason:"Nessun giocatore corrente con questo fantacalcioId, ne' un'associazione manuale già confermata."};
}

// Revisione alpha.3-r5 (quarto audit indipendente, BLOCKER A): OGNI chiave costruita in questo
// modulo per raggruppare/confrontare un'identità multi-componente deve passare da questa funzione —
// mai una concatenazione "a mano" con un delimitatore letterale come "|". r4 consente correttamente
// caratteri ARBITRARI in providerKey (rimosso il divieto di ':', mai stato vietato '|') e mantiene
// sourcePlayerId grezzo (mai escapato) — una concatenazione tipo `a+"|"+b+"|"+c` è quindi ambigua per
// costruzione: (providerKey="a|b", sourcePlayerId="c") e (providerKey="a", sourcePlayerId="b|c")
// producono la STESSA stringa "…|a|b|c" con lo stesso fantacalcioId/season/source, fondendo due
// osservazioni di due vendor REALMENTE diversi (riproduzione esatta dell'audit — il risultato ibrido
// riportato: providerKey="a", sourcePlayerId="c", appearances=20). `JSON.stringify` di un ARRAY di
// valori non è vulnerabile allo stesso problema: la serializzazione JSON di una stringa esegue
// sempre l'escaping dei propri delimitatori (`"`, `\`), quindi due tuple di componenti DIVERSE non
// possono mai produrre lo stesso JSON — l'unico modo per due chiamate di produrre la stessa stringa
// è che gli array di INPUT siano già elemento-per-elemento identici. `null` resta un vero `null`
// nell'array (mai la stringa "null" — coerente con BLOCKER 3/r4), distinto per costruzione da
// qualunque stringa reale, inclusa la stringa vuota.
export function collisionSafeKey(parts){ return JSON.stringify(parts); }

// Revisione alpha.3-r2 (audit blocker 3): l'identità di un'osservazione è VENDOR-SCOPED — due
// provider autorizzati diversi condividono lo stesso `source` ("provider-authorized" è un livello di
// fiducia, mai un'identità di vendor), quindi `providerKey` DEVE far parte della chiave, non solo
// `sourcePlayerId` (che può anche essere vuoto — match solo per fantacalcioId — nel qual caso
// nient'altro distinguerebbe due vendor diversi). Righe senza providerKey (ogni csv-manual/
// transfermarkt-manual, invariate) hanno sempre lo stesso componente `null` qui: comportamento
// legacy identico a prima di questa revisione (mai due providerKey null-per-costruzione confusi con
// un providerKey stringa vuota, che comunque validateProviderKeyFormat rifiuta già a monte).
export function externalRecordKey(r){
  return collisionSafeKey([r.fantacalcioId, r.season, r.source, r.providerKey!=null ? r.providerKey : null, r.sourcePlayerId]);
}

// Campi CUMULATIVI (presenze/gol/... crescono legittimamente nel corso della stagione: un valore
// più recente più ALTO è un aggiornamento atteso, mai un conflitto) — stessa lista, stessa politica,
// sia qui sia in private.merge_external_numeric_field() (0008): un solo contratto, mai due
// implementazioni divergenti fra Artifact e Supabase (revisione r3, blocker 2).
export const EXTERNAL_CUMULATIVE_NUMERIC_FIELDS = ["appearances","starts","minutes","goals","assists","yellowCards","redCards","penaltiesScored","penaltiesMissed","cleanSheets","goalsConceded"];
// Campi testuali descrittivi: un cambiamento è normale (es. trasferimento di mercato) — l'import
// più recente vince, mai un conflitto da tracciare. Stessa lista di private.merge_external_text_field().
export const EXTERNAL_TEXT_FIELDS = ["nome","sourceUrl","team","competition","notes"];

// Revisione alpha.3-r2 (audit blocker 1/2/3 su alpha.3-r1 — vedi
// PROVIDER_ENRICHMENT_DESIGN_alpha3_r1.md e l'equivalente PL/pgSQL in
// private.reconcile_external_records(), 0010_provider_observation_versioning.sql, corretta IN PLACE
// perché non ancora applicata).
//
// BLOCKER 2 (confronto lessicografico di stringhe): providerObservedAt è validato con Date.parse()
// ma la r1 lo confrontava/ordinava come STRINGA — "2026-01-01T01:00:00+02:00" (le 00:00 UTC) risulta
// lessicograficamente MAGGIORE di "2026-01-01T00:00:00Z" (le 00:00 UTC, lo STESSO istante) solo
// perché la cifra "1" > "0" nella rappresentazione testuale, anche quando l'istante reale è identico
// o precedente. Ogni confronto/ordinamento qui passa ORA da providerObservedAtEpochMs() — mai la
// stringa grezza — che usa Date.parse() (che interpreta correttamente l'offset) per ottenere un
// istante realmente comparabile, esattamente come una colonna timestamptz Postgres fa nativamente
// lato SQL (mai stato il problema lì — vedi 0010).
function providerObservedAtEpochMs(record){
  if(!record || record.providerObservedAt==null) return null;
  const ms = Date.parse(record.providerObservedAt);
  return Number.isNaN(ms) ? null : ms;
}
// Tempo "effettivo" di un record ai soli fini dell'ORDINAMENTO del fold: providerObservedAt (istante
// reale) quando disponibile, altrimenti importedAt — fallback invariato per le righe senza metadata
// provider (comportamento legacy identico a prima di questa revisione).
function effectiveTimeMs(record){
  const providerMs = providerObservedAtEpochMs(record);
  if(providerMs!=null) return providerMs;
  const importedMs = record && record.importedAt!=null ? Date.parse(record.importedAt) : NaN;
  return Number.isNaN(importedMs) ? 0 : importedMs;
}

// BLOCKER 3 (identità non vendor-scoped): utility di identità — providerKey + providerObservationId
// insieme, MAI il solo observation id (due vendor diversi potrebbero riusare per coincidenza lo
// stesso identificativo). Esportata per uso diretto nei test/diagnostica; il fold sotto NON la usa
// più come "via rapida" che bypassa l'ordinamento causale (vedi BLOCKER 1 sotto) — una volta che
// externalRecordKey() è vendor-scoped (sopra), tutti i record che raggiungono un singolo gruppo sono
// già garantiti dello STESSO vendor, quindi l'ordinamento causale puro (coalesce(providerObservedAt,
// importedAt)) è già sufficiente e corretto senza bisogno di distinguere "stessa osservazione" da
// "osservazione diversa" per la decisione di merge stessa.
export function isSameProviderObservation(acc, incoming){
  if(!incoming.providerKey || !acc.providerKey || incoming.providerKey!==acc.providerKey) return false;
  if(!incoming.providerObservationId || !acc.providerObservationId || incoming.providerObservationId!==acc.providerObservationId) return false;
  const accMs = providerObservedAtEpochMs(acc), incomingMs = providerObservedAtEpochMs(incoming);
  if(accMs==null || incomingMs==null) return false; // senza un istante reale su ENTRAMBI i lati, mai presumere "non-stale"
  return incomingMs >= accMs;
}
// Solo l'IDENTITÀ (providerKey+providerObservationId), mai il confronto temporale — usata dal fold
// sotto per il ramo BLOCKER A (providerObservedAt UGUALE), dove è proprio l'identità a decidere quale
// dei due percorsi (receipt_seq deterministico vs euristica di fallback) si applica.
function isSameProviderObservationIdentity(acc, member){
  return !!acc.providerKey && !!member.providerKey && acc.providerKey===member.providerKey &&
    !!acc.providerObservationId && !!member.providerObservationId && acc.providerObservationId===member.providerObservationId;
}

// BLOCKER 1 (fold non realmente cronologico quando esiste un record "active"): la r1 inizializzava
// l'accumulatore dal record `active` (che può essere quello con l'osservazione PIÙ recente) e poi
// scorreva `chronological` saltando solo quel singolo oggetto — un record stale che precede
// cronologicamente l'active veniva quindi comunque valutato, e l'euristica "il valore deve aumentare"
// (calcolando di fatto il MASSIMO fra i valori del gruppo, indipendentemente dall'ordine di fold)
// applicava erroneamente il suo valore più alto come se fosse un aggiornamento legittimo.
//
// Correzione: l'identità stabile dell'output (il suo `id`, per continuità in UI/API attraverso
// reconcile successivi) resta SEPARATA dalla decisione sull'ordine causale dei valori — mai
// sacrificata per correggere l'ordering, per esplicita richiesta dell'audit. Il fold dei VALORI parte
// SEMPRE dal record cronologicamente più vecchio (mai da `identitySeed`) e cammina in avanti: se
// ENTRAMBI accumulatore e membro corrente hanno un providerObservedAt reale, un membro con istante
// PIÙ VECCHIO di quanto già accumulato è scartato INTERAMENTE — nessun campo, né numerico né
// testuale, viene mai toccato, indipendentemente dal valore o dall'observation id — mentre un membro
// con istante pari o successivo viene applicato DIRETTAMENTE (fiducia piena nell'orologio del
// provider, che è l'intero scopo di questi metadati: sostituire l'euristica "il valore deve
// aumentare" con un ordine causale reale). Quando l'istante reale manca su almeno un lato (righe
// csv-manual/transfermarkt-manual, o un gruppo misto), si ricade SULL'EURISTICA INVARIATA
// (incremento=aggiornamento legittimo, decremento=regressione registrata) — comportamento legacy
// identico a prima di questa revisione.
//
// Revisione alpha.3-r3 (audit blocker A su r2): providerObservedAt UGUALE fra accumulatore e membro
// corrente era gestito come "pari o successivo" -> applicato DIRETTAMENTE, quindi l'ULTIMO elemento
// processato in quell'ordine (il cui tie-break era `a.id<b.id`, un UUID CASUALE) vinceva sempre —
// riproduzione dell'audit: stessa observation (providerKey+providerObservationId), stesso
// providerObservedAt, payload diverso (es. appearances 10 vs 3) -> il risultato dipendeva da quale
// id locale/UUID fosse alfabeticamente minore, mai da un'informazione reale. Corretto: (1) il
// tie-break dell'ordinamento cronologico usa ora receiptSeq (un contatore di RICEZIONE reale e
// monotono, mai un UUID/id casuale — vedi assignReceiptSequences()); (2) per la STESSA observation
// (providerKey+providerObservationId) con providerObservedAt identico: payload IDENTICO (stesso
// providerPayloadChecksum) è un vero no-op idempotente (mai una scrittura); payload DIVERSO applica
// la ripubblicazione con receiptSeq maggiore (ricevuta DOPO), mai quella con id/UUID minore; (3) per
// OSSERVAZIONI DIVERSE che coincidono per puro caso sullo stesso providerObservedAt: policy
// esplicita fail-closed — MAI inventare un ordine causale dall'identità/UUID, si ricade
// sull'euristica di fallback INVARIATA, esattamente come se l'istante reale non fosse disponibile.
function decideProviderFoldAction(acc, r){
  const accMs = providerObservedAtEpochMs(acc), rMs = providerObservedAtEpochMs(r);
  if(accMs==null || rMs==null) return "fallback"; // istante reale mancante su almeno un lato: legacy invariato.
  if(rMs < accMs) return "skip"; // causalmente più vecchio: MAI applicato, nessuna eccezione.
  if(rMs > accMs) return "apply"; // causalmente più recente: fiducia piena nell'orologio del provider.
  // rMs === accMs (BLOCKER A):
  if(isSameProviderObservationIdentity(acc, r)){
    if(acc.providerPayloadChecksum && r.providerPayloadChecksum && acc.providerPayloadChecksum===r.providerPayloadChecksum){
      return "skip"; // stesso payload: idempotent retry, vero no-op.
    }
    const accSeq = receiptSeqOf(acc), rSeq = receiptSeqOf(r);
    return rSeq > accSeq ? "apply" : "skip"; // deterministico via receiptSeq reale, mai id/UUID.
  }
  // Observation DIVERSE, stesso istante dichiarato: fail-closed, mai un ordine causale inventato.
  return "fallback";
}

export function mergeExternalRecordGroup(records, warnings){
  if(records.length<=1) return {record: records[0], changed:false};
  const chronological = records.slice().sort((a,b)=>{
    const ta = effectiveTimeMs(a), tb = effectiveTimeMs(b);
    if(ta!==tb) return ta-tb;
    return receiptSeqOf(a) - receiptSeqOf(b); // ordine di RICEZIONE reale, mai un UUID/id casuale.
  });
  // Identità stabile (SOLO l'id del record risultante) — MAI il seme dei valori (vedi sopra).
  const identitySeed = records.find(r=>r.active) || chronological[0];

  const acc = Object.assign({}, chronological[0]);
  for(let i=1; i<chronological.length; i++){
    const r = chronological[i];
    const action = decideProviderFoldAction(acc, r);
    if(action==="skip") continue;
    if(action==="apply"){
      EXTERNAL_CUMULATIVE_NUMERIC_FIELDS.forEach(f=>{ if(r[f]!=null) acc[f] = r[f]; });
      EXTERNAL_TEXT_FIELDS.forEach(f=>{ if(r[f]!=null && r[f]!=="") acc[f] = r[f]; });
    }else{
      // Fallback euristico invariato (nessun istante reale affidabile su almeno un lato, O due
      // osservazioni diverse coincidenti sullo stesso providerObservedAt — BLOCKER A, policy
      // fail-closed sopra).
      EXTERNAL_CUMULATIVE_NUMERIC_FIELDS.forEach(f=>{
        const existing = acc[f], incoming = r[f];
        if(incoming==null) return; // niente da aggiungere
        if(existing==null){ acc[f] = incoming; return; } // completa un campo prima mancante
        if(incoming === existing) return; // nessun cambiamento
        if(incoming > existing){ acc[f] = incoming; return; } // aggiornamento cumulativo legittimo
        // incoming < existing: possibile regressione — si conserva il valore più alto già noto, MAI
        // una sovrascrittura silenziosa verso il basso; la divergenza viene sempre registrata.
        warnings.push({type:"external-statistics-regression", fantacalcioId:acc.fantacalcioId, season:acc.season, source:acc.source, sourcePlayerId:acc.sourcePlayerId, field:f, existingValue:existing, incomingValue:incoming,
          message:"Il campo \""+f+"\" per \""+externalRecordKey(acc)+"\" è diminuito rispetto al valore già noto ("+existing+" -> "+incoming+"): conservato il valore più alto, divergenza registrata per revisione."});
      });
      EXTERNAL_TEXT_FIELDS.forEach(f=>{
        const existing = acc[f], incoming = r[f];
        const incomingEmpty = incoming==null || incoming==="";
        if(incomingEmpty) return;
        const existingEmpty = existing==null || existing==="";
        if(existingEmpty || incoming!==existing){ acc[f] = incoming; }
      });
    }
    // Metadata provider: SEMPRE coerenti con l'osservazione realmente applicata in questo passo (mai
    // un residuo di un record scartato per staleness — quello non arriva mai qui, per gli `skip`
    // sopra). receiptSeq avanza al massimo reale visto finora — mai un UUID/id casuale.
    acc.providerKey = r.providerKey!=null ? r.providerKey : acc.providerKey;
    acc.providerObservationId = r.providerObservationId!=null ? r.providerObservationId : acc.providerObservationId;
    acc.providerPayloadChecksum = r.providerPayloadChecksum!=null ? r.providerPayloadChecksum : acc.providerPayloadChecksum;
    if(r.providerObservedAt!=null) acc.providerObservedAt = r.providerObservedAt;
    acc.receiptSeq = Math.max(receiptSeqOf(acc), receiptSeqOf(r));
  }

  acc.id = identitySeed.id;
  return {record: acc, changed:true};
}

export function detectDuplicateExternalIdAssociations(catalogState){
  const warnings = [];
  const bySourceAndId = new Map();
  (catalogState.players||[]).forEach(p=>{
    if(!p.externalIds) return;
    externalIdEntries(p).forEach(entry=>{
      const sourcePlayerId = entry.value;
      if(!sourcePlayerId) return;
      // Revisione alpha.3-r4 (obiettivo architetturale + multi-vendor identity): la chiave include
      // ora providerKey ESPLICITAMENTE — due vendor DIVERSI che condividono lo stesso id grezzo su
      // due giocatori diversi sono una situazione LEGITTIMA (mai un'ambiguità), non più distinguibile
      // dalla sola uguaglianza del valore ora che sourcePlayerId non è più namespacizzato.
      // Revisione alpha.3-r5 (BLOCKER A, quarto audit): collisionSafeKey(), mai una concatenazione
      // "|" — stesso identico problema/stessa identica fix di externalRecordKey() sopra.
      const k = collisionSafeKey([entry.source, entry.providerKey!=null ? entry.providerKey : null, sourcePlayerId]);
      if(!bySourceAndId.has(k)) bySourceAndId.set(k, {source: entry.source, providerKey: entry.providerKey, sourcePlayerId, players:[]});
      bySourceAndId.get(k).players.push(p);
    });
  });
  bySourceAndId.forEach(entry=>{
    if(entry.players.length<=1) return;
    warnings.push({type:"external-id-duplicate-association", source:entry.source, sourcePlayerId:entry.sourcePlayerId,
      message:"L'identificativo \""+entry.sourcePlayerId+"\" su "+entry.source+" risulta associato a più giocatori correnti ("+entry.players.map(p=>p.nome).join(", ")+"): resta ambiguo finché non correggi manualmente una delle due associazioni."});
  });
  return {warnings};
}

/** Reconciler centrale (porting verbatim di reconcileAllExternalRecords, beta.6). */
export function reconcileAllExternalRecords(catalogState){
  const warnings = [];
  let maintenanceChanged = false;

  const pool = (catalogState.externalStatistics||[]).map(r=>({rec:r, from:"active"}))
    .concat((catalogState.unassociatedExternalStatistics||[]).map(r=>({rec:r, from:"queue"})));

  const invalidGroups = new Map();
  const resolvedGroups = new Map();
  // Traccia, per ciascuna combo (fantacalcioId|season), quale gruppo (externalRecordKey)
  // era attivo PRIMA di questo reconcile — indipendentemente dall'esito della risoluzione
  // corrente (matched/invalid/identity-conflict/ambiguous). Necessario per distinguere
  // "nessuna scelta precedente" da "la fonte scelta è diventata non valida": in quest'ultimo
  // caso non deve MAI scattare l'attivazione automatica di un'altra fonte come fallback.
  const previousActiveGroupKeyByCombo = new Map();

  pool.forEach(item=>{
    const rec = normalizeExternalStatisticsRecord(item.rec);
    // Solo un record che proveniva GIA' dallo store attivo (non un record appena importato,
    // che di default nasce con active:true come valore neutro pre-riconciliazione) rappresenta
    // una scelta precedente reale dell'utente.
    if(item.from==="active" && rec.active){
      // Revisione alpha.3-r5 (BLOCKER A): collisionSafeKey() anche qui — fantacalcioId/season sono
      // già canonicalmente vincolati altrove (season passa sempre da parseSeasonKey), ma questo
      // modulo non usa MAI più una concatenazione "a mano" per NESSUNA chiave multi-componente,
      // senza eccezioni, per non lasciare un secondo punto da riverificare in futuro.
      previousActiveGroupKeyByCombo.set(collisionSafeKey([rec.fantacalcioId, rec.season]), externalRecordKey(rec));
    }
    let invalidReason = null;
    if(!EXTERNAL_SOURCE_WHITELIST.includes(rec.source)) invalidReason = "source non consentita: \""+rec.source+"\"";
    else if(!parseSeasonKey(rec.season)) invalidReason = "seasonKey non canonica: \""+rec.season+"\"";
    if(invalidReason){
      const k = externalRecordKey(rec);
      if(!invalidGroups.has(k)) invalidGroups.set(k, {reason:invalidReason, records:[]});
      invalidGroups.get(k).records.push({rec, from:item.from});
      return;
    }
    const resolution = resolveExternalRecordPlayer(catalogState, rec);
    // Revisione r4 (audit, blocker 1): playerId fa parte del contratto canonico condiviso con
    // SupabaseCatalogRepository (che lo legge dalla vera colonna external_statistics.player_id) —
    // qui viene popolato quando la risoluzione trova un giocatore reale, mai lasciato "quasi giusto"
    // solo per i match via externalId (prima la canonicalizzazione di fantacalcioId scattava SOLO
    // in quel caso; ora playerId viene sempre allineato per qualunque esito "matched").
    const canonicalRec = resolution.status==="matched"
      ? Object.assign({}, rec, {
          fantacalcioId: resolution.matchedBy==="externalId" ? resolution.player.fantacalcioId : rec.fantacalcioId,
          playerId: resolution.player.id
        })
      : rec;
    const k = externalRecordKey(canonicalRec);
    if(!resolvedGroups.has(k)) resolvedGroups.set(k, {status:resolution.status, resolution, records:[]});
    resolvedGroups.get(k).records.push({rec:canonicalRec, from:item.from});
  });

  const finalActive = [];
  const finalQueue = [];

  invalidGroups.forEach(group=>{
    const merge = mergeExternalRecordGroup(group.records.map(x=>x.rec), warnings);
    if(merge.changed) maintenanceChanged = true;
    const forced = merge.record.active ? Object.assign({}, merge.record, {active:false}) : merge.record;
    if(forced!==merge.record) maintenanceChanged = true;
    finalQueue.push(forced);
    const wasOnlyQueue = group.records.every(x=>x.from==="queue");
    if(!wasOnlyQueue){
      maintenanceChanged = true;
      warnings.push({type:"external-statistics-invalid", fantacalcioId:forced.fantacalcioId, season:forced.season, source:forced.source,
        message:"Record esterno spostato in coda non associati ("+group.reason+")."});
    }
  });

  resolvedGroups.forEach(group=>{
    const merge = mergeExternalRecordGroup(group.records.map(x=>x.rec), warnings);
    if(merge.changed) maintenanceChanged = true;
    const rec = merge.record;
    if(group.status==="matched"){
      const wasOnlyActive = group.records.every(x=>x.from==="active");
      if(!wasOnlyActive) maintenanceChanged = true;
      finalActive.push(rec);
      return;
    }
    const forced = rec.active ? Object.assign({}, rec, {active:false}) : rec;
    finalQueue.push(forced);
    const wasOnlyQueue = group.records.every(x=>x.from==="queue");
    if(!wasOnlyQueue || forced!==rec) maintenanceChanged = true;
    if(!wasOnlyQueue){
      const type = group.status==="identity-conflict" ? "external-statistics-identity-conflict"
        : group.status==="ambiguous" ? "external-statistics-ambiguous"
        : "external-statistics-invalid";
      warnings.push({type, fantacalcioId:rec.fantacalcioId, season:rec.season, source:rec.source,
        message:"Record esterno spostato in coda non associati ("+group.resolution.reason+")."});
    }
  });

  // Decisione dell'attivazione per combo (fantacalcioId|season), a tre stati:
  //  (A) nessuna scelta precedente -> regola normale (un solo matched -> auto-attivo;
  //      più matched -> nessuno attivo, richiede scelta esplicita);
  //  (B) una fonte era attiva ed è ANCORA valida/matched -> resta quella, invariata;
  //  (C) una fonte era attiva ma NON è più valida/matched -> ZERO fonti attive, anche se
  //      restano una o più fonti matched: mai un fallback automatico su un'altra fonte.
  const allByCombo = new Map();
  finalActive.forEach(rec=>{
    const gk = collisionSafeKey([rec.fantacalcioId, rec.season]);
    if(!allByCombo.has(gk)) allByCombo.set(gk, []);
    allByCombo.get(gk).push(rec);
  });

  let finalActiveReconciled = finalActive.slice();
  const setActiveAt = (rec, active)=>{
    if(rec.active===active) return;
    maintenanceChanged = true;
    const idx = finalActiveReconciled.indexOf(rec);
    finalActiveReconciled[idx] = Object.assign({}, rec, {active});
  };

  allByCombo.forEach((group, comboKey)=>{
    const previousGroupKey = previousActiveGroupKeyByCombo.get(comboKey);
    if(previousGroupKey!==undefined){
      const stillPresent = group.some(r=>externalRecordKey(r)===previousGroupKey);
      if(!stillPresent){
        warnings.push({type:"external-statistics-selection-required", fantacalcioId:group[0].fantacalcioId, season:group[0].season,
          message:"La fonte precedentemente selezionata non è più valida: nessuna fonte resa attiva automaticamente fra quelle disponibili ("+group.map(r=>'"'+r.source+'"').join(", ")+") — seleziona quale usare con \"Rendi attiva\"."});
      }
      group.forEach(r=>setActiveAt(r, stillPresent && externalRecordKey(r)===previousGroupKey));
      return;
    }
    if(group.length===1){
      setActiveAt(group[0], true);
      return;
    }
    warnings.push({type:"external-statistics-selection-required", fantacalcioId:group[0].fantacalcioId, season:group[0].season,
      message:"Più fonti sono disponibili per lo stesso giocatore+stagione ("+group.map(r=>'"'+r.source+'"').join(", ")+"): nessuna resa attiva automaticamente — seleziona quale usare con \"Rendi attiva\"."});
    group.forEach(r=>setActiveAt(r, false));
  });

  catalogState.externalStatistics = finalActiveReconciled;
  catalogState.unassociatedExternalStatistics = finalQueue;

  const duplicateAssociations = detectDuplicateExternalIdAssociations(catalogState);
  warnings.push(...duplicateAssociations.warnings);
  return {warnings, maintenanceChanged};
}

export function findExternalStatsFor(player, seasonKey, catalogState){
  if(player.fantacalcioId==null) return null;
  return (catalogState.externalStatistics||[]).find(e=> e.fantacalcioId===player.fantacalcioId && e.season===seasonKey && e.active) || null;
}
export function listExternalStatsFor(player, seasonKey, catalogState){
  if(player.fantacalcioId==null) return [];
  return (catalogState.externalStatistics||[]).filter(e=> e.fantacalcioId===player.fantacalcioId && e.season===seasonKey);
}
export function setActiveExternalSource(catalogState, recordId){
  const matches = catalogState.externalStatistics.filter(e=>e.id===recordId);
  if(matches.length===0) return {ok:false, error:"Record esterno non trovato."};
  if(matches.length>1) return {ok:false, error:"Errore di integrità: più record esterni condividono lo stesso identificativo (\""+recordId+"\"). Nessuna modifica applicata."};
  const target = matches[0];
  catalogState.externalStatistics = catalogState.externalStatistics.map(e=>{
    if(e.fantacalcioId!==target.fantacalcioId || e.season!==target.season) return e;
    return Object.assign({}, e, {active: e.id===recordId});
  });
  return {ok:true, activatedId: recordId, fantacalcioId: target.fantacalcioId, season: target.season};
}
